import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import TuneIcon from "@mui/icons-material/Tune";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ContentArea from "../components/content-area/Content";
import SettingsIconPng from "../assets/icons/setting.png";
import { useLocalization } from "../context/LocalizationContext";
import { Locale } from "../utils/Locales";
import LocalText from "../components/common/LocalText";
import { useLocalText } from "../components/common/useLocalText";
import { useToast } from "../context/ToastContext";

const MENU = [
  {
    id: "localization",
    label: "Localization",
    description: "Choose the display language for the application.",
    icon: <LanguageIcon sx={{ fontSize: 20 }} />,
  },
  {
    id: "general",
    label: "General",
    description: "Configure general application settings.",
    icon: <TuneIcon sx={{ fontSize: 20 }} />,
  },
  {
    id: "notifications",
    label: "Notifications",
    description: "Manage notification preferences and alerts.",
    icon: <NotificationsNoneIcon sx={{ fontSize: 20 }} />,
  },
];

const selectSx = {
  bgcolor: "white",
  borderRadius: "8px",
  color: "#1e3a5f",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(10,50,109,0.18)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#5383b3" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#5383b3" },
  "& .MuiSvgIcon-root": { color: "#4a6f96" },
};

const ComingSoon = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      gap: 1,
      py: 6,
      opacity: 0.45,
    }}
  >
    <Typography variant="h6" sx={{ color: "#1e3a5f", fontWeight: 700 }}>
      Coming Soon
    </Typography>
    <Typography variant="body2" sx={{ color: "#4a6f96" }}>
      This section is under development.
    </Typography>
  </Box>
);

export default function Settings() {
  const { locale, setLocale, availableLocales } = useLocalization();
  const { toast } = useToast();
  const [tempLocale, setTempLocale] = useState(locale);
  const [activeSection, setActiveSection] = useState("localization");
  const snackMessage = useLocalText("Settings saved successfully.");

  // Once the full locale list loads from the API, if the stored locale is no
  // longer in the list (e.g. it was removed from the space), fall back to the
  // first available option so the Select never shows an out-of-range value.
  useEffect(() => {
    if (availableLocales.length > 0 && !availableLocales.includes(tempLocale)) {
      setTempLocale(availableLocales[0]);
    }
  }, [availableLocales]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (activeSection === "localization") {
      setLocale(tempLocale);
      toast.success(snackMessage);
    }
  };

  const active = MENU.find((m) => m.id === activeSection);

  return (
    <ContentArea title="Settings" icon={SettingsIconPng}>
      {/* Two-panel layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid rgba(10,50,109,0.12)",
          boxShadow: "0 2px 12px rgba(10,50,109,0.08)",
          minHeight: { xs: 300, sm: 400, md: 480 },
        }}
      >
        {/* ── Left nav ── */}
        <Box
          sx={{
            width: { xs: "100%", sm: 220 },
            flexShrink: 0,
            bgcolor: "#0a326d",
            display: "flex",
            flexDirection: "column",
            p: 1.5,
          }}
        >
          <List disablePadding sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, gap: 0.5, overflowX: { xs: "auto", sm: "unset" } }}>
            {MENU.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <Tooltip key={item.id} title={item.label} placement="bottom" disableInteractive>
                  <ListItemButton
                    onClick={() => setActiveSection(item.id)}
                    sx={{
                      borderRadius: "10px",
                      px: { xs: 1, sm: 1.5 },
                      py: 1,
                      whiteSpace: "nowrap",
                      justifyContent: { xs: "center", sm: "flex-start" },
                      bgcolor: isActive ? "rgba(255,255,255,0.14)" : "transparent",
                      transition: "background-color 0.18s ease",
                      "&:hover": { bgcolor: isActive ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.07)" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? "white" : "rgba(255,255,255,0.5)",
                        minWidth: { xs: 0, sm: 34 },
                        transition: "color 0.18s ease",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? "white" : "rgba(255,255,255,0.6)",
                        sx: { display: { xs: "none", sm: "block" } },
                      }}
                    />
                    {/* Active indicator bar — desktop only */}
                    {isActive && (
                      <Box
                        sx={{
                          width: 3,
                          height: 20,
                          bgcolor: "#5383b3",
                          borderRadius: "2px",
                          flexShrink: 0,
                          display: { xs: "none", sm: "block" },
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Box>

        {/* ── Right content ── */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#EAF0F6",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Scrollable body */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: "24px",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(10,50,109,0.2)",
                borderRadius: 4,
              },
            }}
          >
            {/* Section header */}
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: "#1e3a5f", mb: 0.25, lineHeight: 1.3 }}
            >
              {active?.label}
            </Typography>
            <Typography variant="caption" sx={{ color: "#4a6f96" }}>
              {active?.description}
            </Typography>

            {/* Divider */}
            <Box
              sx={{
                height: "1px",
                background: "rgba(10,50,109,0.18)",
                my: 2,
              }}
            />

            {/* Localization section */}
            {activeSection === "localization" && (
              <Box sx={{ maxWidth: { xs: '100%', md: 440 } }}>
                <Typography variant="body2" fontWeight={600} sx={{ color: "#1e3a5f", mb: 1 }}>
                  <LocalText>Language</LocalText>
                </Typography>
                <Select
                  value={tempLocale}
                  onChange={(e) => setTempLocale(e.target.value)}
                  size="small"
                  fullWidth
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: { style: { maxHeight: 240 } },
                  }}
                  sx={selectSx}
                >
                  {availableLocales.map((o) => (
                    <MenuItem key={o} value={o}>
                      {Locale[o] ?? o}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {activeSection !== "localization" && <ComingSoon />}
          </Box>

          {/* Footer with Save button */}
          <Box
            sx={{
              px: "24px",
              py: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid rgba(10,50,109,0.1)",
              bgcolor: "white",
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={
                (activeSection === "localization" && tempLocale === locale) ||
                activeSection !== "localization"
              }
              sx={{
                px: 3,
                py: 0.75,
                fontWeight: 600,
                borderRadius: "8px",
                fontSize: 13,
                bgcolor: "#0a326d",
                "&:hover": { bgcolor: "#1a4a8a" },
                "&.Mui-disabled": { bgcolor: "rgba(10,50,109,0.25)", color: "white" },
              }}
            >
              <LocalText>Save Changes</LocalText>
            </Button>
          </Box>
        </Box>
      </Box>

    </ContentArea>
  );
}
